// caster-cache.ts — shared, settled reads for window.FateTide.casterCache.

import type { CasterArr } from '../shared/array-types';
import { ftWarnIfShapeStale, itemsCol, encounterCreatureField, inventoryField } from '../shared/array-types';
import { compileFetchedEntity, entityBasePending } from '../shared/compile-fetched-entity';
import { readSavedResponse, fetchSavedResponse } from '../shared/read-saved-response';
import { ftCurrentTruthSink, TruthSinkRefusal } from '../shared/truth-sink';
import { withAsyncDeadline } from '../shared/async-deadline';

type UnknownRecord = Record<string, unknown>;
type PermissionValue = UnknownRecord;
type InventoryPermissionMap = Map<string, PermissionValue>;

interface InventoryNode extends UnknownRecord {
    permissions?: PermissionValue | null;
    inventory_in_container?: unknown;
    inventory_id?: unknown;
}

interface CasterBlobRecord extends CasterArr {
    item_layer_types?: unknown;
    items?: UnknownRecord;
    inventory?: unknown;
    player_characters?: unknown;
    permissions?: { player?: boolean; campaign?: boolean };
}

function asRecord(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

// ── ENCOUNTER-BLOB ALIAS SEED (encounter-arr-canonical-pivot §4.2) ───────────────────────────────
// The encounter blob in window.FateTide.encounterCache already carries every encounter creature
// compiled by the browser (encounter_creatures_arr), so a casterCache miss for an in-encounter
// creature does not need another get_caster_arr.php base read.
// We set the SAME object reference into casterCache — the owner-locked alias model: encounter
// mutations (encounter_update / drags) and caster mutations (caster_update_delta) stay visible
// through both reads, with zero copies and zero drift between "the caster" and "the creature on
// the map". resyncCaster() (caster-delta.ts) opts OUT via skipEncounterSeed: a delta version gap
// must heal from fresh server truth, not from a possibly pre-action encounter entry.

// A row is a FULL compiled caster blob (not a lean/shell row) when it carries its own non-empty
// item_layer_types bucket and a resolvable item_type — mirrors object-card find-cached-item isFull().
function isFullCasterBlob(node: unknown): node is CasterBlobRecord {
    const record = asRecord(node);
    if (!record) return false;
    const ilt = asRecord(record.item_layer_types);
    if (!ilt || Object.keys(ilt).length === 0) return false;
    return !!(itemsCol(record, 'item_type') || encounterCreatureField(record.encounter_creatures, 'item_type'));
}

/**
 * unified-viewer-scoping Chunk 2 — preserve a viewer's seed-correct creature `permissions`.
 *
 * The broadcast wire is viewer-AGNOSTIC: the server strips actor-stamped creature `permissions`
 * before fan-out (ft_object_api_strip_actor_creature_permissions). The per-viewer-CORRECT value
 * comes from the page-load seed (get_encounter_arr / get_caster_arr) and from get_caster_arr refetches.
 * So whenever a stripped row replaces / re-aliases a cached row, carry the prior row's `permissions`
 * forward — otherwise an actor's whole-row push would drop the viewer's own affordances. Render-gate
 * only; the server independently re-authorizes every action. Creature-only — nested inventory
 * permissions are a separate server cascade (Chunk 3) and are left untouched.
 *
 * Takes the prior permissions VALUE (captured before an in-place delete+reassign) so it works both
 * for the delete/reassign sites and the two-object realias/standalone-set sites.
 */
function ftCarryPermissionsForward(freshRow: unknown, prevPermissions: unknown): void {
    const record = asRecord(freshRow);
    const permissions = asRecord(prevPermissions);
    if (record && record.permissions == null && permissions) record.permissions = permissions;
}

/**
 * unified-viewer-scoping Chunk 3 — the INVENTORY analog of ftCarryPermissionsForward.
 *
 * Inventory permissions are stripped off the broadcast (ft_object_api_strip_actor_inventory_permissions)
 * at every nesting depth, so a stripped blob's inventory nodes arrive WITHOUT `permissions`. The
 * per-viewer-correct value lives on the prior cached blob's same-instance node, matched by inventory_id
 * (NOT the template item_id, which repeats across instances). These two helpers preserve it across a
 * whole-blob swap / row adopt: collect the old node→permissions map, then fill any stripped new node.
 *
 * The walk is TARGETED (not a full deep-walk of the megabyte blob): it descends only the inventory-
 * bearing slots — encounter_inventories_arr, each creature row's `inventory`, a caster blob's root
 * `inventory`, and each node's `inventory_in_container` (recursively). An inventory node's own
 * `inventory` IDENTITY bucket is an object, not a 0-indexed list, so it is never mis-walked as a list.
 */
function ftInvId(node: InventoryNode): string {
    let v: unknown = inventoryField(node, 'inventory_id');
    if (v === undefined || v === null || v === '') {
        v = Array.isArray(node.inventory_id) ? node.inventory_id[0] : node.inventory_id;
    }
    return v ? String(v).toUpperCase() : '';
}

function ftVisitInventoryNodes(blob: unknown, visit: (node: InventoryNode) => void): void {
    const root = asRecord(blob);
    if (!root) return;
    const walkList = (list: unknown): void => {
        if (!Array.isArray(list)) return;
        for (const inv of list) {
            const inventory = asRecord(inv) as InventoryNode | null;
            if (!inventory) continue;
            visit(inventory);
            walkList(inventory.inventory_in_container);
        }
    };
    walkList(root.encounter_inventories_arr);
    const cres = root.encounter_creatures_arr;
    if (Array.isArray(cres)) {
        for (const c of cres) {
            const creature = asRecord(c);
            if (creature) walkList(creature.inventory);
        }
    }
    walkList(root.inventory); // a single caster_update blob carries its inventory at the root
}

function ftCollectInventoryPermissions(oldBlob: unknown): InventoryPermissionMap {
    const map: InventoryPermissionMap = new Map();
    ftVisitInventoryNodes(oldBlob, (inv) => {
        const permissions = asRecord(inv.permissions);
        if (!permissions) return;
        const id = ftInvId(inv);
        if (id) map.set(id, permissions);
    });
    return map;
}

/** Fill each stripped (permissions-less) inventory node in `newBlob` from a pre-collected map. */
function ftApplyInventoryPermissions(newBlob: unknown, map: InventoryPermissionMap): void {
    if (!asRecord(newBlob) || map.size === 0) return;
    ftVisitInventoryNodes(newBlob, (inv) => {
        if (inv.permissions !== undefined && inv.permissions !== null) return;
        const prev = map.get(ftInvId(inv));
        if (prev !== undefined && prev !== null) inv.permissions = prev;
    });
}

/**
 * Carry seed-correct inventory permissions from `oldBlob` onto the stripped nodes of `newBlob`. Use at
 * sites where the old blob is still intact at call time (the full encounter-blob swap). The clobber
 * sites (adopt: old keys are deleted before the new content lands) must instead snapshot with
 * ftCollectInventoryPermissions BEFORE the delete and re-apply with ftApplyInventoryPermissions after.
 */
function ftCarryInventoryPermissionsForward(newBlob: unknown, oldBlob: unknown): void {
    ftApplyInventoryPermissions(newBlob, ftCollectInventoryPermissions(oldBlob));
}

// Bucket field as a bare scalar (descalared) with a legacy [0]-wrapped fallback.
function bucketScalar(bucket: unknown, key: string): string {
    const v = asRecord(bucket)?.[key];
    const out = Array.isArray(v) ? v[0] : v;
    return out === null || out === undefined ? '' : String(out);
}

/**
 * Find a FULL compiled creature row for `casterId` inside any cached encounter blob. Every
 * encounter-owned creature is keyed by encounter_creature_id; a PC's player_characters.item_id
 * deliberately equals that value and does not create a second lookup namespace.
 */
function findCasterInEncounterCache(casterId: string): CasterArr | null {
    if (!casterId) return null;
    const want = casterId.toUpperCase();
    const encCache = window.FateTide?.encounterCache;
    if (!encCache || typeof encCache.values !== 'function') return null;

    // Exact encounter_creature_id is unique per placed creature, including player characters.
    for (const enc of encCache.values()) {
        const list = enc.encounter_creatures_arr;
        if (!Array.isArray(list)) continue;
        for (const row of list) {
            if (row === null || typeof row !== 'object') continue;
            const ecId = String(
                encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? ''
            ).toUpperCase();
            if (ecId === want && isFullCasterBlob(row)) return row as CasterArr;
        }
    }

    return null;
}

/**
 * Alias an encounter-blob creature row into casterCache under `casterId`. Mirrors the one piece of
 * ft_object_api_hydrate() the encounter path doesn't run: the display-name override
 * (player_name ?? encounter_creature_name → items.item_name), which panes read for the host name
 * (e.g. passive-abilities hostDisplayName). No _meta.shape_v stamp rides along — ftWarnIfShapeStale
 * is warn-only and the row came from an already-shape-checked encounter fetch. Returns true on seed.
 */
function seedCasterFromEncounterCache(casterId: string): boolean {
    const row = findCasterInEncounterCache(casterId) as CasterBlobRecord | null;
    if (!row) return false;
    const displayName = bucketScalar(row.player_characters, 'player_name')
        || bucketScalar(row.encounter_creatures, 'encounter_creature_name');
    if (displayName) {
        row.items = row.items ?? {};
        row.items.item_name = displayName;
    }
    window.FateTide?.casterCache?.set(casterId, row);
    ftDebugLog('caster', { seededFromEncounterCache: casterId });
    return true;
}

window.ftSeedCasterFromEncounterCache = seedCasterFromEncounterCache;

/**
 * Adopt freshly-pushed/fetched caster content INTO the canonical encounter row — the
 * encounter-arr-canonical-pivot single-store rule: when a creature is attached to a cached FULL
 * encounter blob, `encounter_creatures_arr[i]` IS the store, and a full caster_update / resync
 * fetch must not install a competing copy that overrides it. The row object's identity is
 * preserved (contents swapped in place), so casterCache under encounter_creature_id,
 * encounterCache and open panes see the new content with zero re-pointing
 * and zero drift. Returns the canonical row (re-set into casterCache under `casterId` for the
 * cache-miss case), or null when the creature isn't in any cached full encounter blob — the
 * caller then falls back to the plain casterCache replace (standalone/out-of-encounter path).
 */
function adoptCasterBlobIntoEncounterRow(casterId: string, fresh: CasterArr): CasterArr | null {
    const row = findCasterInEncounterCache(casterId);
    if (!row) return null;
    if (row !== fresh) {
        // Carry the seed-correct permissions across the in-place swap when `fresh` (a stripped
        // broadcast row) lacks them. A get_caster_arr refetch arrives per-viewer-correct, so it
        // supplies its own and this is a no-op for that path. [unified-viewer-scoping Chunk 2]
        // Snapshot the nested inventory permissions BEFORE the clobber, then re-apply after — the
        // caster blob carries its inventory and the broadcast strips it too. [Chunk 3]
        const prevPermissions = row.permissions;
        const prevInvPerms = ftCollectInventoryPermissions(row);
        for (const k of Object.keys(row)) delete row[k];
        Object.assign(row, fresh);
        ftCarryPermissionsForward(row, prevPermissions);
        ftApplyInventoryPermissions(row, prevInvPerms);
    }
    window.FateTide?.casterCache?.set(casterId, row as CasterArr);
    ftDebugLog('caster', { adoptedIntoEncounterRow: casterId });
    return row as CasterArr;
}

async function settleCasterReads<T>(read: () => Promise<T>, signal?: AbortSignal): Promise<T> {
    for (let attempt = 1; attempt <= 3; attempt++) {
        try {
            signal?.throwIfAborted();
            return await read();
        } catch (error) {
            // Named exhaustion has already used the shared read's budget. The compatibility
            // wrapper must not multiply three POSTs into nine.
            if (asRecord(error)?.name === 'CasterReadSuperseded'
                || !String(error).includes('Caster snapshot superseded while reading')) throw error;
            if (attempt === 3) break;
            await new Promise<void>((resolve, reject) => {
                const done = (): void => { signal?.removeEventListener('abort', abort); resolve(); };
                const abort = (): void => { clearTimeout(timer); reject(signal?.reason); };
                const timer = setTimeout(done, 750);
                signal?.addEventListener('abort', abort, { once: true });
                if (signal?.aborted) abort();
            });
        }
    }
    const exhausted = new Error('Caster snapshot superseded while reading');
    exhausted.name = 'CasterReadSuperseded';
    throw exhausted;
}

// Cache-miss callers share one base read per caster, including its retry budget.
type CasterFetch = { request: Promise<void>; startedAt: number; participant?: Promise<void>; participantTimedOut?: boolean; superseded?: boolean; replacement?: CasterFetch };
const inFlightCasterFetches = new Map<string, CasterFetch>();

function fetchAndCacheCaster(casterId: string, opts?: { skipEncounterSeed?: boolean; throwOnError?: boolean; participantDeadline?: boolean }): Promise<void> {
    const sink = ftCurrentTruthSink();
    if (sink) return sink.entity(casterId).then(local => {
        if (!local) throw new TruthSinkRefusal('training-grounds:caster-not-in-store');
        const caster = local as CasterArr;
        const canonical = adoptCasterBlobIntoEncounterRow(casterId, caster);
        if (!canonical) window.FateTide?.casterCache?.set(casterId, caster);
        window.ftRerenderPcSheet?.(casterId);
    }).catch(error => {
        if (opts?.throwOnError) throw error;
        // TruthSinkRefusal already reports the named gap; fire-and-forget hydration must settle.
        if (!(error instanceof TruthSinkRefusal)) new TruthSinkRefusal(`training-grounds:caster-read:${String(error)}`);
    });
    // Cache-miss callers (subscribeToCaster, the info-panel/sheet panes, XZ token hydration) resolve
    // instantly from the encounter blob when the creature is in a cached encounter — no compile POST.
    if (!opts?.skipEncounterSeed && seedCasterFromEncounterCache(casterId)) {
        // Heal-on-access [unified-viewer-scoping Chunk 2]: a genuinely-new creature (post-load spawn /
        // first-sight) arrives on the viewer-agnostic wire WITHOUT `permissions` and has no prior
        // cached value to carry forward, so the row we just seeded lacks it. The per-viewer seed
        // ALWAYS stamps a permissions object (all-false for an enemy), so an ABSENT key uniquely marks
        // a stripped new row — fall through to the per-viewer get_caster_arr fetch (deduped) to stamp
        // the correct value. Until it lands the row renders fail-closed (no affordances), which is safe.
        const seededRow = window.FateTide?.casterCache?.get(casterId);
        const seededHasPerms = seededRow?.permissions != null;
        if (seededHasPerms) {
            if (entityBasePending(seededRow)) {
                const compiled = compileFetchedEntity(seededRow).then(() => { window.ftRerenderPcSheet?.(casterId); });
                return opts?.throwOnError ? compiled : compiled.catch(err => { console.error('Failed to compile caster data:', err); });
            }
            return Promise.resolve();
        }
        // else: stripped new row — continue to the fetch below.
    }
    const consume = (pending: CasterFetch): Promise<void> => {
        // A cast joining an ordinary read gets only the original request's remaining budget.
        // Its short timeout must not cancel that ordinary caller's cold read.
        if (opts?.participantDeadline && !pending.participant) {
            const signal = AbortSignal.timeout(Math.max(0, Math.ceil(15000 - (performance.now() - pending.startedAt))));
            pending.participant = new Promise<void>((resolve, reject) => {
                const abort = (): void => { pending.participantTimedOut = true; reject(signal.reason); };
                signal.addEventListener('abort', abort, { once: true });
                pending.request.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
            });
        }
        const request = opts?.participantDeadline ? pending.participant! : pending.request;
        return opts?.throwOnError ? request : request.catch(err => { console.error('Failed to fetch caster data:', err); });
    };
    const pending = inFlightCasterFetches.get(casterId);
    if (pending && !(opts?.participantDeadline && pending.participantTimedOut)) {
        return consume(pending);
    }
    // A retry replaces an expired cast view of an ordinary read. Its late answer must neither
    // install over the replacement nor clear that replacement's in-flight slot.
    if (pending) pending.superseded = true;
    const pendingRead: CasterFetch = { request: Promise.resolve(), startedAt: 0 };
    if (pending) pending.replacement = pendingRead;
    inFlightCasterFetches.set(casterId, pendingRead);
    const request = (async () => {
        try {
            // Keep the slot and participant budget across backoff and every fresh POST.
            pendingRead.startedAt = performance.now();
            // Ordinary recovery includes body decoding and compilation. Keep the
            // measured cold-start allowance, but never retain its shared slot forever.
            const participantSignal = opts?.participantDeadline ? AbortSignal.timeout(15000) : undefined;
            await withAsyncDeadline(signal => settleCasterReads(async () => {
                // A timed-out participant may already have started a replacement. Share that read
                // even if it has finished, rather than issuing another POST for the late pane.
                if (pendingRead.replacement) return pendingRead.replacement.request;
                const beforeRow = findCasterInEncounterCache(casterId) ?? window.FateTide?.casterCache?.get(casterId);
                const encounterId = encounterCreatureField<string>(beforeRow?.encounter_creatures, 'encounter_id');
                const beforeEncounter = encounterId ? window.FateTide?.encounterCache?.get(encounterId) : undefined;
                const beforeEncounterVersion = beforeEncounter?._ws_version;
                const beforeCasterVersion = beforeRow?._ws_version;
                const body = JSON.stringify({ caster_id: casterId, include_catalog_scope: true,
                    ...window.ftHeldEntityCatalogHints?.(String(asRecord(beforeRow?._entity_base)?.catalog_version ?? '')) });
                let payload: unknown;
                try {
                    const response = await fetchSavedResponse(`${BASE_URL}options/tools/encounter_creatures/get_caster_arr.php`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, body, signal
                    });
                    if (!response.ok) throw new Error('Failed to fetch caster');
                    payload = await readSavedResponse(response);
                } catch (error) {
                    // Body reads may throw AbortError even when the request's reason is TimeoutError.
                    throw signal?.aborted ? signal.reason : error;
                }
                const casterPayload = asRecord(asRecord(payload)?.caster_arr);
                if (casterPayload) {
                    const casterArr = casterPayload as CasterArr;
                    // These channels have independent counters. A changed encounter invalidates this
                    // read even if its caster-channel version appears current, including across compilation.
                    const assertCurrent = (): void => {
                        signal.throwIfAborted();
                        const held = findCasterInEncounterCache(casterId) ?? window.FateTide?.casterCache?.get(casterId);
                        const heldEncounter = encounterId ? window.FateTide?.encounterCache?.get(encounterId) : undefined;
                        if (pendingRead.superseded || heldEncounter !== beforeEncounter || heldEncounter?._ws_version !== beforeEncounterVersion
                            || (typeof held?._ws_version === 'number' && typeof casterArr._ws_version === 'number'
                                && casterArr._ws_version < held._ws_version)
                            || (typeof casterArr._ws_version !== 'number'
                                && (held !== beforeRow || held?._ws_version !== beforeCasterVersion))) {
                            throw new Error('Caster snapshot superseded while reading');
                        }
                    };
                    assertCurrent();
                    ftWarnIfShapeStale(casterArr, 'get_caster_arr refetch');
                    await compileFetchedEntity(casterArr);
                    try { assertCurrent(); }
                    catch (error) { window.ftDiscardInventoryBase?.(casterArr); throw error; }
                    // Single-store rule: an in-encounter creature's fetched truth lands INSIDE the
                    // canonical encounter row (identity preserved), never as a competing copy.
                    // skipEncounterSeed only opts out of READING the row as a seed — fresh server
                    // content flowing INTO it is the desired direction (delta resync included).
                    const canonicalRow = adoptCasterBlobIntoEncounterRow(casterId, casterArr);
                    if (!canonicalRow) {
                        window.FateTide?.casterCache?.set(casterId, casterArr);
                        window.ftRebindEntityBase?.(casterArr, casterArr);
                    } else if (canonicalRow !== casterArr && casterArr._entity_base) {
                        window.ftRebindEntityBase?.(casterArr, canonicalRow);
                    }
                    ftDebugLog('caster', { dataCached: casterId });
                    // Control switches can already have painted a provisional roster row while this
                    // request was pending. Installing the full spell/ability catalog alone leaves those
                    // done-marked panes stuck on that partial paint. Refresh only this caster's mounted
                    // panes; a late response must never repaint the newly selected actor with old data.
                    window.ftRerenderPcSheet?.(casterId);
                } else {
                    throw new Error('Invalid caster recovery payload');
                }
            }, signal), 180000, participantSignal);
        } finally {
            if (inFlightCasterFetches.get(casterId) === pendingRead) inFlightCasterFetches.delete(casterId);
        }
    })();
    pendingRead.request = request;
    if (opts?.participantDeadline) pendingRead.participant = request;
    return consume(pendingRead);
}

window.fetchAndCacheCaster = fetchAndCacheCaster;

/** Compatibility for compound readers; individual caster reads now settle themselves. */
export function withSettledCasterReads<T>(read: () => Promise<T>): Promise<T> {
    return settleCasterReads(read);
}

export {
    fetchAndCacheCaster, adoptCasterBlobIntoEncounterRow, findCasterInEncounterCache,
    ftCarryPermissionsForward, ftCarryInventoryPermissionsForward,
    ftCollectInventoryPermissions, ftApplyInventoryPermissions,
};
